import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { arcgisToGeoJSON } from '@terraformer/arcgis';
import type { Geometry } from 'geojson';
import {
  aorsToHierarchy,
  buildEpaDemoAor,
  FEMA_REGION_META,
  getEpaRrtAorsFallback,
} from 'constants/epaRrtAors';
import type { AreaOfResponsibility } from 'types/aor';
import type { AORHierarchyPayload } from 'components/MapPage/CorporateViewTabs/AorsTab/utils/schema';
import { WGS84_SPATIAL_REFERENCE } from '../utils/spatialReference';

const FEMA_REGIONS_FEATURE_SERVER_URL =
  'https://gis.fema.gov/arcgis/rest/services/FEMA/RegHQs/FeatureServer/2';

let cachedQuery: Promise<AreaOfResponsibility[]> | null = null;
let cachedGroupGuid: string | null = null;

function asRegionNumber(attrs: Record<string, unknown>): number | null {
  const raw = attrs.cnt_region ?? attrs.CNT_REGION;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const label = String(attrs.region ?? attrs.REGION ?? '');
  const match = label.match(/(VIII|VII|III|II|IX|IV|VI|X|V|I|\d+)\s*$/i);
  if (!match) return null;
  const token = match[1].toUpperCase();
  const roman: Record<string, number> = {
    I: 1,
    II: 2,
    III: 3,
    IV: 4,
    V: 5,
    VI: 6,
    VII: 7,
    VIII: 8,
    IX: 9,
    X: 10,
  };
  const fromRoman = roman[token];
  if (fromRoman != null) return fromRoman;
  const parsed = Number(token);
  return Number.isFinite(parsed) ? parsed : null;
}

function isPolygonGeom(
  geom: Geometry
): geom is Extract<Geometry, { type: 'Polygon' | 'MultiPolygon' }> {
  return geom.type === 'Polygon' || geom.type === 'MultiPolygon';
}

async function queryFemaRegionAorsUncached(
  groupGuid: string
): Promise<AreaOfResponsibility[]> {
  const layer = new FeatureLayer({ url: FEMA_REGIONS_FEATURE_SERVER_URL });
  await layer.load();
  const { features } = await layer.queryFeatures({
    where: '1=1',
    returnGeometry: true,
    outFields: ['region', 'cnt_region'],
    outSpatialReference: WGS84_SPATIAL_REFERENCE,
  });

  const byNumber = new Map<number, Geometry>();
  for (const feature of features) {
    if (!feature.geometry) continue;
    const number = asRegionNumber(feature.attributes ?? {});
    if (number == null) continue;
    const geoJson = arcgisToGeoJSON(feature.geometry.toJSON()) as Geometry;
    if (isPolygonGeom(geoJson)) {
      byNumber.set(number, geoJson);
    }
  }

  const aors = FEMA_REGION_META.flatMap((meta) => {
    const geom = byNumber.get(meta.number);
    if (!geom || !isPolygonGeom(geom)) return [];
    return [
      buildEpaDemoAor({
        groupGuid,
        id: meta.id,
        name: `FEMA Region ${meta.roman}`,
        description: `FEMA Region ${meta.roman}`,
        geom,
      }),
    ];
  });

  if (aors.length !== FEMA_REGION_META.length) {
    throw new Error(
      `FEMA region query returned ${aors.length} of ${FEMA_REGION_META.length} regions`
    );
  }

  return aors;
}

export async function queryFemaRegionAors(
  groupGuid: string
): Promise<AreaOfResponsibility[]> {
  if (cachedQuery && cachedGroupGuid === groupGuid) {
    return cachedQuery;
  }

  cachedGroupGuid = groupGuid;
  cachedQuery = queryFemaRegionAorsUncached(groupGuid).catch((error) => {
    console.warn(
      '[EPA FEMA AORs] Live FEMA region query failed; using bbox fallback.',
      error
    );
    cachedQuery = null;
    cachedGroupGuid = null;
    return getEpaRrtAorsFallback(groupGuid);
  });
  return cachedQuery;
}

export async function queryFemaRegionAorsByHierarchy(
  groupGuid: string
): Promise<AORHierarchyPayload> {
  return aorsToHierarchy(await queryFemaRegionAors(groupGuid));
}
